import type { IntlShape } from 'react-intl';

import { IconColor } from '~/eds/src/types/IconColor';
import { StatusType } from '~/eds/src/types/StatusType';
import { TextColor } from '~/eds/src/types/TextColor';
import { TextVariant } from '~/eds/src/types/TextVariant';
import { UserAction } from '~/eds/src/types/UserAction';
import { CrmType } from '~/enums/CrmType';
import { EsignatureType } from '~/enums/EsignatureType';
import { StorageProviderType } from '~/enums/StorageProviderType';
import { SyncPairStatusType } from '~/enums/SyncPairStatusType';
import { Filter } from '~/evifields/src/types/Filter';

export const checkFlagIncluded = (integration: any) => {
  return !('featureFlag' in integration) || integration.featureFlag;
};

export const getStatusTextProps = (status: any, intl: IntlShape) => {
  const base = {
    iconSpacing: 's' as const,
    variant: 'body-medium' as TextVariant,
  };

  switch (status) {
    case 'connected':
      return {
        ...base,
        icon: 'status-success',
        text: intl.formatMessage({
          id: 'Integrations.connected',
          defaultMessage: 'Connected',
          description: 'Connected status text',
        }),
        iconColor: 'status.success' as IconColor,
        textColor: 'status.success' as TextColor,
      };
    case 'error':
      return {
        ...base,
        icon: 'status-danger',
        text: intl.formatMessage({
          id: 'Integrations.error',
          defaultMessage: 'Validation error',
          description: 'Validation error status text',
        }),
        iconColor: 'status.danger' as IconColor,
        textColor: 'status.danger' as TextColor,
      };
    default:
      return undefined;
  }
};

export const getChipStatusProps = (
  statuses: string[],
  chipTranslationKeys: Record<
    string,
    { id: string; label: string; isAcronym?: boolean }
  >,
) => {
  return statuses.map((status) => {
    const translationConfig = chipTranslationKeys[status];

    return {
      text: translationConfig?.label,
      status: 'info' as StatusType,
      value: translationConfig?.id,
    };
  });
};

export const cardMatchesFilters = (filters: Filter[]) => {
  const domainFilter = filters.find((f) => f.fieldId === 'domain');
  const categoriesFilter = filters.find((f) => f.fieldId === 'categories');
  const accessFilter = filters.find((f) => f.fieldId === 'access');

  const selectedDomains = (domainFilter?.values || []).filter(
    (v): v is string => typeof v === 'string',
  );
  const selectedCategories = (categoriesFilter?.values || []).filter(
    (v): v is string => typeof v === 'string',
  );
  const selectedAccess = (accessFilter?.values || []).filter(
    (v): v is string => typeof v === 'string',
  );

  return (card: any, sectionName: string) => {
    const cardMatchesDomain =
      selectedDomains.length === 0 ||
      card.chips?.some((chip: any) =>
        selectedDomains.some(
          (domain) => chip.value?.toLowerCase() === domain.toLowerCase(),
        ),
      );

    const sectionMatchesCategory =
      selectedCategories.length === 0 ||
      selectedCategories.some(
        (category) => category.toLowerCase() === sectionName.toLowerCase(),
      );

    const cardMatchesAccess =
      selectedAccess.length === 0 ||
      selectedAccess.some(
        (access) =>
          card.actions?.[0]?.text?.toLowerCase() === access.toLowerCase(),
      );

    return cardMatchesDomain && sectionMatchesCategory && cardMatchesAccess;
  };
};

export const cardMatchesSearch = (
  card: any,
  sectionLabel: string,
  sectionName: string,
  search?: string,
) => {
  const searchLower = search?.toLowerCase().trim() ?? '';
  const searchableText = [
    card.label,
    card.description,
    card.name,
    card.provider,
    sectionLabel,
    sectionName.toLowerCase(),
  ]
    .filter(Boolean)
    .map((text) => String(text).toLowerCase())
    .join(' ');

  return searchableText.includes(searchLower);
};

export const resetSyncPairAuthCode = (syncPair: any) => ({
  ...syncPair,
  code: null,
});

export const hasErrorStatus = (statuses: SyncPairStatusType[]) => {
  const errorStatuses = [
    SyncPairStatusType.AuthError,
    SyncPairStatusType.DataAccessError,
    SyncPairStatusType.OutOfSpaceError,
    SyncPairStatusType.OtherError,
    SyncPairStatusType.ConfigError,
  ];

  return statuses.some((status) => errorStatuses.includes(status));
};

export const hasActiveStatus = (statuses: SyncPairStatusType[]) => {
  const activeStatuses = [
    SyncPairStatusType.Active,
    SyncPairStatusType.ActiveSync,
    SyncPairStatusType.Syncing,
  ];

  return statuses.some((status) => activeStatuses.includes(status));
};

export const hasSyncingStatus = (statuses: SyncPairStatusType[]) => {
  const syncingStatuses = [
    SyncPairStatusType.Syncing,
    SyncPairStatusType.ActiveSync,
  ];

  return statuses.some((status) => syncingStatuses.includes(status));
};

export const activeEsignatureIntegration = (integrations: any[]) => {
  return integrations.find(
    (integration: any) => integration.id && !integration.id.startsWith('mock-'),
  );
};

export const isEsignatureIntegrationActive = (
  integrations: any[],
  provider: EsignatureType,
) => {
  const activeIntegration = activeEsignatureIntegration(integrations);
  if (!activeIntegration) return false;

  return (
    activeIntegration.provider === provider ||
    activeIntegration.providerName === provider
  );
};

export const isOtherEsignatureIntegrationActive = (
  integrations: any[],
  currentProvider: EsignatureType,
) => {
  const activeIntegration = activeEsignatureIntegration(integrations);
  if (!activeIntegration) return false;

  return (
    activeIntegration.provider !== currentProvider &&
    activeIntegration.providerName !== currentProvider
  );
};

export const shouldDisableEsignatureConfigure = (
  integrations: any[],
  currentProvider: EsignatureType,
) => {
  const currentIntegration = integrations.find(
    (integration: any) =>
      integration?.provider === currentProvider ||
      integration?.providerName === currentProvider,
  );
  const isCurrentActive =
    currentIntegration?.id && !currentIntegration.id.startsWith('mock-');

  return (
    !isCurrentActive &&
    isOtherEsignatureIntegrationActive(integrations, currentProvider)
  );
};

export const getActionStatus = (
  provider: string | StorageProviderType | CrmType,
  accessConditions: {
    configure: UserAction;
    reconnect: UserAction;
    configured: UserAction;
    syncing: UserAction;
    remove: UserAction;
  },
  syncPairs: any[],
) => {
  const { configure, reconnect, configured, syncing, remove } =
    accessConditions;

  if (!provider || !syncPairs || syncPairs.length === 0) {
    return configure;
  }

  const filteredPairs = syncPairs.filter((sp) => {
    return String(sp.provider).toLowerCase() === String(provider).toLowerCase();
  });

  if (filteredPairs.length === 0) {
    return configure;
  }

  if (provider === CrmType.Salesforce) {
    const hasError = filteredPairs.some((sp) => sp.error != null);
    const hasId = filteredPairs.some((sp) => sp.id != null);

    return hasError ? reconnect : hasId ? configured : configure;
  }

  if (
    provider === EsignatureType.Docusign ||
    provider === EsignatureType.Adobesign
  ) {
    const isThisProviderActive = isEsignatureIntegrationActive(
      filteredPairs,
      provider,
    );
    return isThisProviderActive ? remove : configure;
  }

  const statuses = filteredPairs.map((sp) => sp.status);

  if (hasErrorStatus(statuses)) {
    return reconnect;
  }

  if (hasSyncingStatus(statuses)) {
    return syncing;
  }

  if (!hasActiveStatus(statuses)) {
    return configure;
  }

  return configured;
};
